import React, { useState } from 'react';
import { SearchIcon, BriefcaseIcon, ClockIcon, MapPinIcon, DollarSignIcon, CheckIcon } from 'lucide-react';
import BackButton from '../../components/UI/BackButton';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
const opportunities = [{
  id: 1,
  title: 'Marketing Assistant',
  company: 'Literary Launch Co.',
  location: 'Remote',
  type: 'Full-time',
  category: 'marketing',
  salary: '$45,000 - $60,000',
  description: 'Support authors with marketing campaigns and social media management.',
  requirements: ['2+ years of marketing experience', 'Strong social media skills', 'Experience with email marketing', 'Excellent communication skills'],
  posted: '2 days ago',
  applicationDeadline: '2023-12-15'
}, {
  id: 2,
  title: 'Author Assistant',
  company: 'Author Success Team',
  location: 'Remote',
  type: 'Part-time',
  category: 'assistant',
  salary: '$25-35/hour',
  description: 'Provide administrative and creative support to multiple authors.',
  requirements: ['Strong organizational skills', 'Experience with scheduling', 'Proficiency in Microsoft Office', 'Ability to manage multiple projects'],
  posted: '5 days ago',
  applicationDeadline: '2023-12-20'
}, {
  id: 3,
  title: 'Social Media Coordinator',
  company: 'BookBuzz Agency',
  location: 'Hybrid',
  type: 'Contract',
  category: 'marketing',
  salary: '$40-50/hour',
  description: 'Create and manage social media content for author clients.',
  requirements: ['3+ years of social media management', 'Content creation skills', 'Analytics experience', 'Knowledge of publishing industry'],
  posted: '1 week ago',
  applicationDeadline: '2023-12-30'
}];
const categories = [{
  id: 'all',
  name: 'All Categories'
}, {
  id: 'marketing',
  name: 'Marketing'
}, {
  id: 'assistant',
  name: 'Author Assistant'
}, {
  id: 'publicity',
  name: 'Publicity'
}, {
  id: 'research',
  name: 'Research'
}];
const types = [{
  id: 'all',
  name: 'All Types'
}, {
  id: 'full-time',
  name: 'Full-time'
}, {
  id: 'part-time',
  name: 'Part-time'
}, {
  id: 'contract',
  name: 'Contract'
}, {
  id: 'freelance',
  name: 'Freelance'
}];
const typeBadge: Record<string, string> = {
  'Full-time': 'bg-green-100 text-green-800',
  'Part-time': 'bg-blue-100 text-blue-800',
  Contract: 'bg-amber-100 text-amber-800',
  Freelance: 'bg-purple-100 text-purple-800'
};
const SupportOpportunities = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const filtered = opportunities.filter(o => {
    const matchesSearch = o.title.toLowerCase().includes(searchQuery.toLowerCase()) || o.company.toLowerCase().includes(searchQuery.toLowerCase()) || o.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || o.category === selectedCategory;
    const matchesType = selectedType === 'all' || o.type.toLowerCase() === selectedType;
    return matchesSearch && matchesCategory && matchesType;
  });
  const selectClass = `px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`;
  return <div className={`min-h-screen ${dk.page}`} data-id="element-13715" data-mp="2ueui2yrki88y">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-13716" data-mp="2pg001z1fcnpw">
        <div className="mb-8" data-id="element-13717" data-mp="2pdn8cu5v6yzn">
          <div className="mb-4" data-id="element-13718" data-mp="2kgwsa6u283e8">
            <BackButton data-id="element-13719" data-mp="2kfvie5os5my3" />
          </div>
          <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-13720" data-mp="2ges25i2tlowc">
            Support Opportunities
          </h1>
          <p className={`mt-2 text-lg ${dk.textSecondary}`} data-id="element-13721" data-mp="355pkyjlbucpf">
            Find opportunities to support authors and contribute to their
            success
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 flex flex-col md:flex-row gap-4" data-id="element-13722" data-mp="2wdxgqqmwbzta">
          <div className="flex-1 relative" data-id="element-13723" data-mp="30epsprn5kit9">
            <SearchIcon className={`h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 ${dk.textFaint}`} data-id="element-13724" data-mp="30do6qs4lmbwr" />
            <input type="text" placeholder="Search opportunities..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`} data-id="element-13725" data-mp="2mh16oghcn5mm" />
          </div>
          <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className={`w-full md:w-48 ${selectClass}`} data-id="element-13726" data-mp="bmnauxz37d7h">
            {categories.map(c => <option key={c.id} value={c.id} data-id="element-13727" data-mp="2072hf7kft3nd">
                {c.name}
              </option>)}
          </select>
          <select value={selectedType} onChange={e => setSelectedType(e.target.value)} className={`w-full md:w-48 ${selectClass}`} data-id="element-13728" data-mp="1w34iawv43sub">
            {types.map(t => <option key={t.id} value={t.id} data-id="element-13729" data-mp="2juiejdwdw8kk">
                {t.name}
              </option>)}
          </select>
        </div>

        {/* Opportunities */}
        <div className="space-y-6" data-id="element-13730" data-mp="2exjjmt4wbxlb">
          {filtered.map(o => <div key={o.id} className={`rounded-lg shadow-md p-6 border ${dk.card}`} data-id="element-13731" data-mp="2stx2o6p2w71w">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between" data-id="element-13732" data-mp="2nu0vxzozy1in">
                <div className="flex-1" data-id="element-13733" data-mp="yhfk3cf6pv3l">
                  <h2 className={`text-xl font-semibold ${dk.textPrimary}`} data-id="element-13734" data-mp="tim5fgo5vy1u">
                    {o.title}
                  </h2>
                  <div className="mt-2 space-y-1.5" data-id="element-13735" data-mp="2tlnckjkce6d6">
                    <div className={`flex items-center gap-2 ${dk.textSecondary}`} data-id="element-13736" data-mp="2yjf61i0my0jh">
                      <BriefcaseIcon className={`h-4 w-4 ${dk.textFaint}`} data-id="element-13737" data-mp="33i9up0laajbi" />
                      {o.company}
                    </div>
                    <div className={`flex items-center gap-2 ${dk.textSecondary}`} data-id="element-13738" data-mp="37lfz02ou60fc">
                      <MapPinIcon className={`h-4 w-4 ${dk.textFaint}`} data-id="element-13739" data-mp="3hiawqjdabwsk" />
                      {o.location}
                    </div>
                    <div className={`flex items-center gap-2 ${dk.textSecondary}`} data-id="element-13740" data-mp="3m8ray5oxjmfz">
                      <DollarSignIcon className={`h-4 w-4 ${dk.textFaint}`} data-id="element-13741" data-mp="3r7kqe3ns1nis" />
                      {o.salary}
                    </div>
                  </div>
                  <p className={`mt-4 ${dk.textSecondary}`} data-id="element-13742" data-mp="42v45yev3b4a">{o.description}</p>
                  <div className="mt-4" data-id="element-13743" data-mp="1olfe3gt87ccq">
                    <h3 className={`font-medium mb-2 ${dk.textPrimary}`} data-id="element-13744" data-mp="1omi5rqydhitp">
                      Requirements:
                    </h3>
                    <ul className="space-y-1.5" data-id="element-13745" data-mp="1joglx3t4hfh6">
                      {o.requirements.map((req, i) => <li key={i} className="flex items-start gap-2" data-id="element-13746" data-mp="1fmlk7y7a502v" data-mpi={i}>
                          <CheckIcon className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-13747" data-mp="1an8nmlqlwen3" />
                          <span className={`text-sm ${dk.textSecondary}`} data-id="element-13748" data-mp="27bey3pa7m19a">
                            {req}
                          </span>
                        </li>)}
                    </ul>
                  </div>
                </div>
                <div className="mt-6 md:mt-0 md:ml-6 flex flex-col items-start md:items-end gap-3" data-id="element-13749" data-mp="1tl1qxp556ql0">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${typeBadge[o.type] ?? rt.badge}`} data-id="element-13750" data-mp="2r0fid0655mub">
                    {o.type}
                  </span>
                  <div className={`flex items-center gap-1 text-sm ${dk.textMuted}`} data-id="element-13751" data-mp="1ulcl514q0ri8">
                    <ClockIcon className="h-4 w-4" data-id="element-13752" data-mp="1zdxddy7zawjn" />
                    Posted {o.posted}
                  </div>
                  <button className={`px-4 py-2 border rounded-md font-medium text-sm transition-colors ${rt.outlineButton}`} data-id="element-13753" data-mp="1kp9jrxhp0wwc">
                    Apply Now
                  </button>
                </div>
              </div>
            </div>)}
          {filtered.length === 0 && <div className={`text-center py-12 rounded-xl border-2 border-dashed ${dk.emptyState}`} data-id="element-13754" data-mp="24bphdqlwe947">
              <p className={`text-lg ${dk.textMuted}`} data-id="element-13755" data-mp="28bznr4dlr1jl">
                No opportunities found matching your criteria.
              </p>
            </div>}
        </div>
      </div>
    </div>;
};
export default SupportOpportunities;